import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { formatPhone } from '../utils/formatPhone';

// A company's public snow removal rates, at /rates/:slug.
//
// Everything here comes from GET /api/public/company/:slug?product=snow. The
// server decides what may be shown: when the owner has switched off public
// pricing (companies.show_public_pricing), it sends no tiers and no matrix, and
// this page says pricing is available on request. The page never infers a price
// the server did not send, so a matrix cell with no price reads "Ask", not $0.
//
// The slug is the same value the intake form link carries (custom_slug or
// company_code), so a "See our rates" link from the form and a Sign up link back
// to it both reuse it as-is.

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};

// A percent add-on (migration 104) has no dollar price: "+10%". A flat one is
// "from" its lowest tier price.
export const addonPriceText = (addon) => (addon.pricing_type === 'percent'
  ? `+${Number(Number(addon.percent).toFixed(2))}%`
  : `from ${money(addon.price)}`);

function RatesPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/public/company/${encodeURIComponent(slug)}`,
          { params: { product: 'snow' } }
        );
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.status === 404
          ? 'We could not find this company. Please check the link.'
          : 'Rates could not be loaded right now. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    const name = data?.company?.name;
    document.title = name ? `${name} snow removal rates` : 'Snow removal rates';
  }, [data]);

  const signUpHref = `/new-customer/${encodeURIComponent(slug)}`;

  if (loading) {
    return (
      <div className="rates-page"><style>{styles}</style>
        <div className="rates-card"><div className="rates-spinner" aria-label="Loading" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rates-page"><style>{styles}</style>
        <div className="rates-card"><h1 className="rates-title">Rates</h1><p className="rates-muted">{error}</p></div>
      </div>
    );
  }

  const company = data.company || {};
  const depth = data.depth_pricing;
  const tiers = Array.isArray(data.pricing_tiers) ? data.pricing_tiers : [];
  const addons = Array.isArray(data.service_addons) ? data.service_addons : [];
  const showPricing = company.show_pricing !== false;
  const premium = depth && Number(depth.on_call_premium_percent) > 0
    ? Number(Number(depth.on_call_premium_percent).toFixed(2))
    : null;
  const contact = [company.phone && formatPhone(company.phone), company.email].filter(Boolean);

  return (
    <div className="rates-page">
      <style>{styles}</style>
      <div className="rates-card">
        <header className="rates-header">
          <h1 className="rates-title">{company.name}</h1>
          {company.tagline && <p className="rates-tagline">{company.tagline}</p>}
          <p className="rates-kicker">Snow removal rates</p>
        </header>

        {!showPricing && (
          <p className="rates-request">
            Pricing is available on request. Sign up below and we will be in touch with a quote.
          </p>
        )}

        {showPricing && depth && (
          <section aria-labelledby="matrix-heading">
            <h2 id="matrix-heading" className="rates-section-title">Price per visit by snow depth</h2>
            <p className="rates-muted rates-note">{depth.note}</p>
            <div className="rates-table-wrap" tabIndex={0} role="region" aria-label="Price per visit by driveway size and snow depth">
              <table className="rates-table">
                <thead>
                  <tr>
                    <th scope="col" className="rates-sticky">Driveway size</th>
                    {depth.bands.map((b) => <th scope="col" key={b.position}>{b.label}</th>)}
                    <th scope="col">Salt</th>
                  </tr>
                </thead>
                <tbody>
                  {depth.tiers.map((t) => (
                    <tr key={t.name}>
                      <th scope="row" className="rates-sticky">
                        <span className="rates-tier-name">{t.name}</span>
                        {t.description && <span className="rates-tier-desc">{t.description}</span>}
                      </th>
                      {t.prices.map((p, i) => <td key={i}>{p === null ? 'Ask' : money(p)}</td>)}
                      <td>{t.salt_price > 0 ? money(t.salt_price) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {premium && (
              <p className="rates-premium">
                On-call service (no seasonal contract) is billed at {premium}% above these rates.
              </p>
            )}
          </section>
        )}

        {showPricing && !depth && tiers.length > 0 && (
          <section aria-labelledby="tiers-heading">
            <h2 id="tiers-heading" className="rates-section-title">Price per visit</h2>
            <ul className="rates-tier-list">
              {tiers.map((t) => (
                <li key={t.name} className="rates-tier-row">
                  <div>
                    <span className="rates-tier-name">{t.name}</span>
                    {t.description && <span className="rates-tier-desc">{t.description}</span>}
                  </div>
                  <div className="rates-tier-prices">
                    <span className="rates-tier-price">{money(t.default_price)}</span>
                    {t.salt_price > 0 && <span className="rates-tier-salt">Salt {money(t.salt_price)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {showPricing && !depth && tiers.length === 0 && (
          <p className="rates-request">Pricing is available on request.</p>
        )}

        {showPricing && addons.length > 0 && (
          <section aria-labelledby="addons-heading">
            <h2 id="addons-heading" className="rates-section-title">Add-ons</h2>
            <ul className="rates-addon-list">
              {addons.map((a) => (
                <li key={a.name}><span>{a.name}</span><span>{addonPriceText(a)}</span></li>
              ))}
            </ul>
          </section>
        )}

        {showPricing && company.description && <p className="rates-description">{company.description}</p>}

        <Link to={signUpHref} className="rates-signup">Sign up for service</Link>
        {showPricing && contact.length > 0 && (
          <p className="rates-muted rates-contact">Questions? {contact.join(' · ')}</p>
        )}
      </div>
    </div>
  );
}

const styles = `
  .rates-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding: 16px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    box-sizing: border-box;
  }
  .rates-card {
    background: #fff;
    border-radius: 16px;
    width: 100%;
    max-width: 760px;
    padding: 24px 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    box-sizing: border-box;
    color: #1f2937;
  }
  .rates-header { text-align: center; margin-bottom: 20px; }
  .rates-title { font-size: 1.6rem; color: #1e3a5f; margin: 0 0 6px; overflow-wrap: anywhere; }
  .rates-tagline { color: #374151; margin: 0 0 6px; }
  .rates-kicker { color: #6b7280; margin: 0; font-size: 0.95rem; }
  .rates-section-title { font-size: 1.05rem; color: #1e3a5f; margin: 20px 0 6px; }
  .rates-muted { color: #6b7280; font-size: 0.875rem; }
  .rates-note { margin: 0 0 10px; }
  .rates-request { background: #eff6ff; border-radius: 10px; padding: 14px; line-height: 1.5; }
  .rates-table-wrap { overflow-x: auto; border: 1px solid #e5e7eb; border-radius: 10px; }
  .rates-table { border-collapse: collapse; width: 100%; font-size: 0.9rem; }
  .rates-table th, .rates-table td { padding: 10px 8px; text-align: right; border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
  .rates-table thead th { background: #f8fafc; color: #374151; font-weight: 600; }
  .rates-table th.rates-sticky { text-align: left; position: sticky; left: 0; background: #fff; white-space: normal; min-width: 108px; }
  .rates-table thead th.rates-sticky { background: #f8fafc; }
  .rates-table tbody tr:last-child th, .rates-table tbody tr:last-child td { border-bottom: none; }
  .rates-tier-name { display: block; font-weight: 600; }
  .rates-tier-desc { display: block; color: #6b7280; font-size: 0.8rem; font-weight: 400; }
  .rates-premium { margin: 10px 0 0; background: #fffbeb; border-radius: 8px; padding: 10px 12px; font-size: 0.9rem; }
  .rates-tier-list, .rates-addon-list { list-style: none; padding: 0; margin: 0; }
  .rates-tier-row { display: flex; justify-content: space-between; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f1f5f9; }
  .rates-tier-prices { text-align: right; white-space: nowrap; }
  .rates-tier-price { display: block; font-weight: 700; font-size: 1.1rem; color: #1e3a5f; }
  .rates-tier-salt { display: block; color: #6b7280; font-size: 0.8rem; }
  .rates-addon-list li { display: flex; justify-content: space-between; gap: 12px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
  .rates-description { margin: 20px 0 0; line-height: 1.5; color: #374151; }
  .rates-signup {
    display: block; text-align: center; margin-top: 24px; padding: 14px;
    background: #2563eb; color: #fff; border-radius: 10px; font-weight: 600; text-decoration: none;
  }
  .rates-signup:hover, .rates-signup:focus { background: #1d4ed8; }
  .rates-contact { text-align: center; margin: 12px 0 0; }
  .rates-spinner { width: 32px; height: 32px; margin: 24px auto; border: 3px solid #e5e7eb; border-top-color: #2563eb; border-radius: 50%; animation: rates-spin 0.8s linear infinite; }
  @keyframes rates-spin { to { transform: rotate(360deg); } }
  @media (min-width: 640px) {
    .rates-page { padding: 32px 20px; }
    .rates-card { padding: 32px; }
    .rates-title { font-size: 2rem; }
  }
`;

export default RatesPage;
